import Paper from '@/components/Paper'
import SubHeaders from '@/components/Headers/SubHeaders'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { FilterBar } from '@/components/ui/data-table-customized'
import { ExcelExportButton } from '@/components/ui/data-table-customized'
import AddIcons from '@/icons/AddIcons'
import { paths } from '@/utils/path'
import { IPermissionProps } from '@/types/shared'
import { useTourHandler } from '@/contexts/tour-handler'
import { useEffect } from 'react'

import { BUSINESS_EXCEL_COLUMNS } from '../../types/utils'
import { useBusinessList } from '../../hooks/use-business-list'
import Statistical from '../Create/Statistical'
import { useBusinessListTour } from '../../hooks/use-business-list-tour'
import { useBusinessListQuickHelp } from '../../hooks/use-business-list-quick-help'

interface BusinessListProps extends IPermissionProps {
    pageType: 'member' | 'hrm'
    hideHeader?: boolean
}

const List = ({
    pageType = 'member',
    hideHeader = false,
}: BusinessListProps) => {
    const {
        businesses,
        pagination,
        stateComponent,
        control,
        setFilter,
        params,
        queryParams,
        searchInputProps,
        selectedKeys,
        setSelectedKeys,
        columns,
        actions,
        selectionActions,
        filterItems,
        filterToggles,
        previewColumns,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        navigate,
        t,
    } = useBusinessList(pageType)

    const isHRM = pageType === 'hrm'

    // Tour hooks
    const { startTour } = useBusinessListTour(pageType)
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Quick help hook
    useBusinessListQuickHelp(pageType)

    // Register tour handler
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    return (
        <div className="space-y-2" data-tour="business-list">
            {!hideHeader && (
                <SubHeaders
                    actions={
                        !isHRM
                            ? [
                                  {
                                      icon: <AddIcons />,
                                      label: t('business-create'),
                                      variant: 'contained',
                                      onClick: () =>
                                          navigate(paths.businessAccountCreate),
                                      'data-tour': 'create-business-btn',
                                  },
                              ]
                            : []
                    }
                    breadcrumbs={[
                        {
                            path: isHRM
                                ? paths.businessesManage
                                : paths.businessAccountList,
                            title: t('business-list'),
                        },
                    ]}
                />
            )}

            {!isHRM && <Statistical />}

            <Paper className="space-y-2">
                <div data-tour="business-filter-bar">
                    <FilterBar
                        storageKey="business-list"
                        control={control}
                        setFilter={setFilter}
                        hideSearch
                        tourPrefix="business-filter"
                        items={filterItems}
                    />
                </div>

                {stateComponent || (
                    <div data-tour="business-data-table">
                        <DataTableCustomized
                            columns={columns}
                            rows={businesses}
                            rowKey={(row) => row.business_id}
                            resizable
                            size="small"
                            autoWidth
                            actions={actions}
                            toolbar={{
                                search: {
                                    ...searchInputProps,
                                    placeholder: t(
                                        'business-search-placeholder'
                                    ),
                                    tourId: 'business-search',
                                },
                                settings: isHRM
                                    ? {
                                          show: true,
                                          storageKey: 'business-list',
                                          filterToggles,
                                          tourId: 'business-settings',
                                          slotComponents: [
                                              <ExcelExportButton
                                                  key="export"
                                                  apiEndpoint="business"
                                                  columns={
                                                      BUSINESS_EXCEL_COLUMNS
                                                  }
                                                  queryParams={queryParams}
                                                  feature="business"
                                              />,
                                          ],
                                      }
                                    : {
                                          show: true,
                                          storageKey: 'business-list',
                                          filterToggles,
                                          tourId: 'business-settings',
                                      },
                            }}
                            selection={
                                isHRM
                                    ? {
                                          selectedKeys,
                                          onSelectionChange: setSelectedKeys,
                                          getRowKey: (row) =>
                                              String(row.business_id),
                                          canSelect: (row) =>
                                              [
                                                  'leader-approved',
                                                  'pending',
                                              ].includes(row.business_status),
                                          actions: selectionActions,
                                          previewColumns,
                                      }
                                    : undefined
                            }
                            sorting={{
                                sortBy: params.sort_by,
                                sortOrder: params.sort_order,
                                onSortChange: handleSortChange,
                            }}
                            pagination={{
                                page: params.page,
                                pageSize: params.page_size,
                                total: pagination?.total || 0,
                                onPageChange: handlePageChange,
                                onPageSizeChange: handlePageSizeChange,
                            }}
                        />
                    </div>
                )}
            </Paper>
        </div>
    )
}

export const BusinessList = List
export default List
